import { defineStore } from "pinia"
import { msalInstance, activeAccount, loginRequest } from "../auth/msalConfig"
import { getMe, type Me } from "../api/client"

interface AuthState {
  signedIn: boolean
  me: Me | null
  loading: boolean
  error: string | null
}

export const useAuthStore = defineStore("auth", {
  state: (): AuthState => ({
    signedIn: false,
    me: null,
    loading: false,
    error: null,
  }),

  getters: {
    hasRole: (s) => (role: string) => s.me?.roles.includes(role) ?? false,
    isAdmin:      (s) => s.me?.roles.includes("mc-admin") ?? false,
    isManagement: (s) => s.me?.roles.includes("mc-management") ?? false,
    displayName:  (s) => s.me?.name || s.me?.upn || "",
  },

  actions: {
    async hydrate() {
      const acct = activeAccount()
      this.signedIn = !!acct
      if (acct && msalInstance.getActiveAccount() === null) {
        msalInstance.setActiveAccount(acct)
      }
      if (this.signedIn) {
        await this.refreshMe()
      }
    },
    async signIn() {
      try {
        this.loading = true
        this.error = null
        await msalInstance.loginRedirect(loginRequest)
      } catch (e) {
        this.error = String(e)
      } finally {
        this.loading = false
      }
    },
    async signOut() {
      const account = activeAccount() ?? undefined
      // Clear MSAL's local cache aggressively (including any stale guest-account
      // tokens) before redirecting to the Microsoft logout endpoint. This stops
      // the browser from auto-picking the same wrong account on the next login.
      sessionStorage.clear()
      await msalInstance.logoutRedirect({
        account,
        postLogoutRedirectUri: window.location.origin + "/",
      })
      this.signedIn = false
      this.me = null
    },
    async refreshMe() {
      try {
        this.loading = true
        this.error = null
        this.me = await getMe()
      } catch (e: unknown) {
        this.error = String(e)
      } finally {
        this.loading = false
      }
    },
  },
})
